import { Capacitor, registerPlugin } from "@capacitor/core";
import { OAuthProvider, signInWithCredential } from "firebase/auth";
import { auth } from "../firebase";

export const APPLE_AUTH_ERROR_CODES = Object.freeze({
  CANCELLED: "APPLE_AUTH_CANCELLED",
  IN_PROGRESS: "APPLE_AUTH_IN_PROGRESS",
  MISSING_IDENTITY_TOKEN: "APPLE_AUTH_MISSING_IDENTITY_TOKEN",
  MISSING_RAW_NONCE: "APPLE_AUTH_MISSING_RAW_NONCE",
});

const AppleAuth = registerPlugin("AppleAuth");
let appleSignInInFlight = null;
let appleFirebaseSignInInFlight = null;

export function isNativeIosAppleAuthAvailable(capacitor = Capacitor) {
  return (
    capacitor.isNativePlatform() === true &&
    capacitor.getPlatform() === "ios"
  );
}

function createAppleAuthError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

export function isAppleAuthCancellation(error) {
  const code = String(error?.code || "");
  return code === APPLE_AUTH_ERROR_CODES.CANCELLED || code === "1001";
}

export function isAppleCredentialCollision(error) {
  return String(error?.code || "") === "auth/account-exists-with-different-credential";
}

export function getAppleAuthErrorTranslationKey(error) {
  const code = String(error?.code || "");

  if (isAppleCredentialCollision(error)) return "login.apple.errors.accountCollision";
  if (
    code === APPLE_AUTH_ERROR_CODES.MISSING_IDENTITY_TOKEN ||
    code === APPLE_AUTH_ERROR_CODES.MISSING_RAW_NONCE
  ) {
    return "login.apple.errors.incompleteResponse";
  }
  if (
    code === "auth/missing-or-invalid-nonce" ||
    code === "auth/invalid-credential" ||
    code === "APPLE_AUTH_INVALID_NONCE"
  ) {
    return "login.apple.errors.invalidNonce";
  }
  if (code === "auth/network-request-failed") return "login.apple.errors.network";
  return "login.apple.errors.generic";
}

export function requestNativeAppleCredential({ nativePlugin = AppleAuth } = {}) {
  if (appleSignInInFlight) return appleSignInInFlight;

  appleSignInInFlight = (async () => {
    const appleResult = await nativePlugin.signIn();
    const identityToken = String(appleResult?.identityToken || "").trim();
    const rawNonce = String(appleResult?.rawNonce || "").trim();

    if (!identityToken) {
      throw createAppleAuthError(
        APPLE_AUTH_ERROR_CODES.MISSING_IDENTITY_TOKEN,
        "Apple did not return an identity token."
      );
    }
    if (!rawNonce) {
      throw createAppleAuthError(
        APPLE_AUTH_ERROR_CODES.MISSING_RAW_NONCE,
        "Apple sign-in did not return the raw nonce."
      );
    }

    const provider = new OAuthProvider("apple.com");
    const credential = provider.credential({ idToken: identityToken, rawNonce });
    return { credential, appleResult };
  })().finally(() => {
    appleSignInInFlight = null;
  });

  return appleSignInInFlight;
}

export function performNativeAppleSignIn({
  nativePlugin = AppleAuth,
  firebaseAuth = auth,
} = {}) {
  if (appleFirebaseSignInInFlight) return appleFirebaseSignInInFlight;
  appleFirebaseSignInInFlight = requestNativeAppleCredential({ nativePlugin })
    .then(({ credential }) => signInWithCredential(firebaseAuth, credential))
    .finally(() => {
      appleFirebaseSignInInFlight = null;
    });
  return appleFirebaseSignInInFlight;
}

export function signInWithNativeApple() {
  if (!isNativeIosAppleAuthAvailable()) {
    return Promise.reject(
      createAppleAuthError("APPLE_AUTH_UNAVAILABLE", "Native Apple sign-in is unavailable.")
    );
  }
  return performNativeAppleSignIn();
}
